// frontend/src/components/common/ItemPickerDialog.jsx
//
// The one modal a merchant picks items in.
//
// What this owns: the dialog shell, the filter row (free-text + product
// category), a server-side grid with checkbox selection, its column LAYOUT
// (who the leftover width goes to — see `columnDefs`), and the
// `[Cancel] [Add (N)]` footer. What the caller owns: which endpoint the rows
// come from, what its own columns beyond the item's identity contain, and what
// confirming means.
//
// ── Two selection modes, because the callers genuinely differ ──
//
// `selectionMode='rule'` — the header checkbox means "every row matching the
//   current filter", including pages the grid never loaded. What reaches
//   `onConfirm` is the SELECTION RULE (`{ids}` or `{select_all, exclude_ids}`)
//   for a bulk endpoint to resolve against the same filter the grid listed
//   with. One request, not a chunked loop that can half-apply. Use it whenever
//   confirming is a server write.
//
// `selectionMode='rows'` — the caller needs the row OBJECTS (it is building
//   local draft state, not posting a rule), so rows the grid never loaded
//   cannot be acted on. The header checkbox ticks and unticks the current
//   page via `scopeHeaderCheckboxToPage`, which is the honest reading of the
//   control when the payload is a materialised list. The caller may open the
//   dialog with rows already ticked (`selected`): what reaches `onConfirm` is
//   then the WHOLE ticked set, so an unticked row is one the merchant dropped.
//
// Both halves live in utils/gridSelection.js; this component only chooses
// between them. AG Grid honours `selectAll: 'currentPage'` only on the
// client-side row model, so under SSRM the confinement is imperative.
//
// ── Changing the filter keeps a rows selection, and starts a rule over ──
// A select-all names the FILTER, so carrying one across would silently come
// to name a different set of rows than the merchant agreed to. A rows
// selection is a list of rows: AG Grid keeps ticks by row id across the reload
// (`DefaultStrategy.toggledNodes`) and keeps the ticked nodes with their data,
// so a search for the next item leaves the earlier picks ticked.
//
// ── The filter is NOT tab-persisted, and does not survive a close ──
// Pages use ItemFilterBar/useItemFilter, which persist per tab so an F5 keeps
// the merchant's view. A modal's filter is scratch: it belongs to one act of
// picking and must not come back the next time the dialog opens.
//
// That reset is structural, not an effect: `PickerBody` holds every piece of
// picking state and is only MOUNTED while the dialog is open, so closing
// discards it and reopening starts clean. It also means the SSRM grid is not
// wired to a datasource while nobody is looking at it.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Typography,
} from '@mui/material';

import { ICON, X } from '../../icons';
import ProductCategoryPicker from './ProductCategoryPicker';
import SearchField from './SearchField';
import { useItemSearchCopy } from '../../features/item-filter';
import { useAgGridTheme } from '../../utils/agGridTheme';
import {
  defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, flexColumn,
} from '../../utils/gridDefaults';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useGridState } from '../../hooks/useGridState';
import { buildPickerQuery, useSsrmGrid } from '../../hooks/useSsrmGrid';
import { translateRequestError } from '../../queries/api';
import { showToast } from './appToastBus';
import { itemIdentityColumns, useIsShopify } from '../grid/productIdentity';
import { NO_SELECTION, scopeHeaderCheckboxToPage, readGridSelection } from '../../utils/gridSelection';
import DialogHeading from './DialogHeading';
import '../../styles/dialog-form.css';
import { GridEmptyState } from '../grid/NoRowsOverlay';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 100;
const GRID_HEIGHT = 420;
const NO_ROWS = Object.freeze({ count: 0, rows: [] });

export default function ItemPickerDialog({
  open,
  onClose,
  title,
  // The record the picker fills, named under the title (DialogHeading).
  subtitle = null,
  // Row source. Anything paginated and `?search=` capable; the grid sends
  // ordering and paging itself (useSsrmGrid).
  endpoint,
  // The caller's own query params. MEMOIZE at the call site — useSsrmGrid
  // re-fetches on identity change.
  params,
  gridStateKey,
  selectionMode = 'rule',
  // Rows mode only: the rows the dialog opens ticked, each `{item_id, ...}`
  // in the caller's column fields. A loaded row with the same id shows these
  // values over the server's, so the grid shows what the caller holds (the
  // order's quantity, not an empty cell). Read once, when the dialog opens.
  selected,
  // Columns appended after the item's identity column(s). A caller wanting an
  // editable Cost or Quantity passes it here; nothing about editing is this
  // component's business beyond wiring `editableGridProps`.
  //
  // They keep the width they declare (or measure): the dialog's leftover width
  // goes to the identity column — see `columnDefs` below. A caller that wants
  // its column to share it spreads `flexColumn()` into the colDef.
  extraColumns,
  // Row key holding the merchant's SKU on image-capable stores. Required, not
  // defaulted: `/api/items/` names it `shopify_sku` and the PO form's
  // supplier-catalog rows name it `sku`, and a default would let a new row
  // source render every SKU blank without anything saying so.
  skuField,
  showProductCategoryFilter = true,
  // (count) => label. Defaults to "Add (N)".
  confirmLabel,
  // (selection) => void | Promise. `selection` carries:
  //   count   — rows selected, the number the footer promised.
  //   payload — rule mode only: `{ids}` or `{select_all, exclude_ids}`.
  //   rows    — rows mode only: every ticked row object, `selected` ones
  //             included. One never loaded comes back as its `selected`
  //             entry.
  //   edits   — `{[itemId]: {[field]: value}}` for the caller's editable
  //             columns; sparse, holding only what was actually typed.
  //   query   — the `?…` this grid listed with, for re-resolving a rule.
  // Closing is the caller's call — it is the one that knows whether the write
  // succeeded.
  onConfirm,
  confirming = false,
  emptyMessage,
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      className="dialog-form dialog-form--wide"
    >
      <DialogTitle>
        <DialogHeading title={title} subtitle={subtitle} />
        <IconButton onClick={onClose} size="small" disabled={confirming} aria-label={t('btn.close')}>
          <X size={ICON.xl} />
        </IconButton>
      </DialogTitle>
      {/* Mounted only while open — see the file header on why the reset is
          structural rather than an effect. */}
      {open && (
        <PickerBody
          endpoint={endpoint}
          params={params}
          gridStateKey={gridStateKey}
          selectionMode={selectionMode}
          selected={selected}
          extraColumns={extraColumns}
          skuField={skuField}
          showProductCategoryFilter={showProductCategoryFilter}
          confirmLabel={confirmLabel}
          onConfirm={onConfirm}
          onClose={onClose}
          confirming={confirming}
          emptyMessage={emptyMessage}
        />
      )}
    </Dialog>
  );
}


function PickerBody({
  endpoint,
  params,
  gridStateKey,
  selectionMode,
  selected,
  extraColumns,
  skuField,
  showProductCategoryFilter,
  confirmLabel,
  onConfirm,
  onClose,
  confirming,
  emptyMessage,
}) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const itemSearch = useItemSearchCopy();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState(gridStateKey);
  const gridApiRef = useRef(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState(null);
  // Rows mode always carries `rows`: with `selected`, confirming an empty
  // selection is how the merchant drops every row the dialog opened with.
  const [selection, setSelection] = useState(
    selectionMode === 'rows' ? NO_ROWS : NO_SELECTION,
  );
  // A REF, not state: nothing renders from it, and `stopEditing()` at confirm
  // time flushes the in-progress cell through onCellValueChanged synchronously
  // — a state update would not be visible to the read two lines later, which is
  // exactly the typed price that must not go missing.
  const editsRef = useRef({});
  // Rows matching the CURRENT filter, from the last datasource response — what
  // a header-checkbox select-all actually covers, and therefore the count the
  // footer must promise. Not the store's total.
  const [filteredRowCount, setFilteredRowCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const ssrmParams = useMemo(() => ({
    ...params,
    search: searchText,
    product_category_id: category?.id ?? null,
  }), [params, searchText, category]);

  const onData = useCallback((data) => {
    if (typeof data?.count === 'number') setFilteredRowCount(data.count);
  }, []);

  // `selected` by row id, read once: the dialog is modal, so the caller's
  // list cannot change while it is open.
  const [preselected] = useState(
    () => new Map((selected ?? []).map((row) => [String(row.item_id), row])),
  );

  // Every loaded row shows what the caller holds for it and then what was
  // typed on it, so a reload after a search does not put the server's values
  // back under a row the merchant already edited.
  const transformRows = useCallback((data) => data.results.map((row) => {
    const id = String(row.item_id);
    return { ...row, ...preselected.get(id), ...editsRef.current[id] };
  }), [preselected]);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint,
    params: ssrmParams,
    pageSize: PAGE_SIZE,
    gridRef: gridApiRef,
    onData,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
    transformRows,
  });

  // Ticked by id, before any row loads: AG Grid ticks each row as its block
  // arrives. The selection event this raises sets the footer count.
  const handleGridReady = useCallback((event) => {
    onGridReady(event);
    if (preselected.size) {
      event.api.setServerSideSelectionState({
        selectAll: false, toggledNodes: [...preselected.keys()],
      });
    }
  }, [onGridReady, preselected]);

  // Rule mode only — see the file header. Driven from the two controls that
  // change the filter, so the reset is part of the interaction that caused it.
  const resetRuleSelection = useCallback(() => {
    if (selectionMode !== 'rule') return;
    gridApiRef.current?.deselectAll();
    setSelection(NO_SELECTION);
    editsRef.current = {};
  }, [selectionMode]);

  const onSearchChange = useCallback((value) => {
    setSearchInput(value);
    resetRuleSelection();
  }, [resetRuleSelection]);

  const onCategoryChange = useCallback((value) => {
    setCategory(value);
    resetRuleSelection();
  }, [resetRuleSelection]);

  // Values typed into the caller's editable columns, keyed by row id then
  // field. Harvested here rather than read back off the row objects at confirm
  // time because rule mode never hands the caller rows — a select-all names
  // rows the grid never loaded — yet the merchant's typed cost on the rows
  // they DID look at still has to reach the write.
  const onCellValueChanged = useCallback((event) => {
    const rowId = String(event.data?.item_id);
    const field = event.colDef?.field;
    if (!field) return;
    editsRef.current = {
      ...editsRef.current,
      [rowId]: { ...editsRef.current[rowId], [field]: event.newValue },
    };
  }, []);

  const onSelectionChanged = useCallback((event) => {
    if (selectionMode === 'rows') {
      scopeHeaderCheckboxToPage(event.api);
      // The ids are the selection; `getSelectedRows()` holds only the rows
      // ticked through a node, so a `selected` row ticked by id and never
      // clicked is read back from `selected`.
      const ids = event.api.getServerSideSelectionState()?.toggledNodes ?? [];
      const ticked = new Map(
        event.api.getSelectedRows().map((row) => [String(row.item_id), row]),
      );
      const rows = ids.map((id) => {
        const row = ticked.get(id) ?? preselected.get(id);
        if (!row) throw new Error(`ItemPickerDialog: ticked row ${id} has no row object`);
        return row;
      });
      setSelection({ count: rows.length, rows });
      return;
    }
    setSelection(readGridSelection(event.api, filteredRowCount));
  }, [selectionMode, filteredRowCount, preselected]);

  const columnDefs = useMemo(() => [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('itemPicker.columns.product'),
      // The item's identity is the one column that swallows the dialog's
      // leftover width: it is what varies in length and what the merchant is
      // scanning. A caller's column (Cost) and, on the non-Shopify shape, the
      // fixed-length item code are measured to their content instead.
      product: { skuField, ...flexColumn(2) },
      codeHeader: t('itemPicker.columns.itemCode'),
      code: { minWidth: 130 },
      nameHeader: t('itemPicker.columns.itemName'),
      name: flexColumn(),
    }),
    ...(extraColumns || []),
  ], [isShopify, t, extraColumns, skuField]);

  const getRowId = useCallback((p) => String(p.data.item_id), []);

  const handleConfirm = useCallback(async () => {
    // Commit a cell still being edited BEFORE reading `edits`. The merchant
    // typing a cost and clicking Add without tabbing out is the normal way to
    // use this dialog, and `stopEditingWhenCellsLoseFocus` resolving that blur
    // before the click handler runs is not something this codebase has
    // verified — so the last typed price would be silently dropped. Same
    // ordering the PO form's save takes, for the same reason.
    gridApiRef.current?.stopEditing?.();
    // `query` rides along so a select-all RULE can be re-resolved server-side
    // against the same filter this grid listed with — built by the same
    // serialiser the datasource URL uses, so the two cannot describe different
    // sets.
    await onConfirm({
      ...selection,
      edits: editsRef.current,
      query: buildPickerQuery(ssrmParams),
    });
  }, [selection, onConfirm, ssrmParams]);

  const label = confirmLabel
    ? confirmLabel(selection.count)
    : t('itemPicker.addCount', { count: selection.count });

  return (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchField
            autoFocus
            label={itemSearch.label}
            placeholder={itemSearch.placeholder}
            value={searchInput}
            onChange={onSearchChange}
            fullWidth={false}
            sx={{ width: 320 }}
          />
          {showProductCategoryFilter && (
            <ProductCategoryPicker
              scopeToStore
              value={category}
              onChange={onCategoryChange}
              label={t('itemPicker.productCategory')}
              allLabel={t('searchFilters.productCategoryAllLabel')}
              sx={{ width: 320 }}
            />
          )}
        </Box>

        <Box sx={{ height: GRID_HEIGHT, flexShrink: 0 }}>
          {gridTheme && (
            <GridEmptyState
              title={emptyMessage || t('itemPicker.empty')}
              filtersActive={!!searchText || !!category}
            >
              <AgGridReact
                {...defaultGridProps}
                {...editableGridProps}
                theme={gridTheme}
                {...gridState}
                sideBar={false}
                columnDefs={columnDefs}
                getRowId={getRowId}
                defaultColDef={defaultColDef}
                columnTypes={columnTypes}
                onGridReady={handleGridReady}
                onGridPreDestroyed={onGridPreDestroyed}
                onSelectionChanged={onSelectionChanged}
                onCellValueChanged={onCellValueChanged}
                rowModelType="serverSide"
                serverSideSortAllLevels={true}
                {...pagination}
                rowSelection={{
                  mode: 'multiRow',
                  checkboxes: true,
                  headerCheckbox: true,
                  // 'all' is the only value AG Grid honours under SSRM —
                  // 'filtered' and 'currentPage' are documented as invalid and
                  // silently behave as 'all'. Rows mode narrows it back to the
                  // current page imperatively in onSelectionChanged.
                  selectAll: 'all',
                  copySelectedRows: false,
                }}
                getContextMenuItems={copyOnlyContextMenuItems}
              />
            </GridEmptyState>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {/* Indeterminate: `confirming` says the caller's write is running,
            not how far it got. */}
        {confirming && (
          <Box sx={{ flex: 1, mr: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              {t('itemPicker.saving')}
            </Typography>
          </Box>
        )}
        <Button variant="text" onClick={onClose} disabled={confirming}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          loading={confirming}
          disabled={!selection.count && !preselected.size}
        >
          {label}
        </Button>
      </DialogActions>
    </>
  );
}
